import { Controller } from "@hotwired/stimulus"

// Primer Brand's AnchorNav, without React.
//
// Three jobs: mark the link whose section is in the middle of the viewport as
// current, stick the bar once the page has scrolled past it, and open a menu
// of the links on narrow screens. The markup is rendered with the first link
// current and the menu closed, so nothing here is needed for a page to work.
export default class extends Controller {
  static targets = ["nav", "inner", "menuButton", "closedIcon", "openIcon", "currentLabel",
                    "linkContainer", "link", "overlay", "spacer"]

  connect() {
    this.initialTop = this.navTarget.getBoundingClientRect().top + window.scrollY
    this.observers = this.linkTargets.map(link => this.observeSection(link)).filter(Boolean)
  }

  disconnect() {
    for (const observer of this.observers) observer.disconnect()
    document.body.classList.remove("AnchorNav--no-bg-scroll")
  }

  // The section counts as current while it crosses the horizontal midline of
  // the viewport; that is what upstream's "-50% 0% -50% 0%" root margin does.
  observeSection(link) {
    const section = document.querySelector(link.getAttribute("href"))
    if (!section) return null

    const observer = new IntersectionObserver(([entry]) => {
      if (entry.isIntersecting) this.setCurrent(link)
    }, { threshold: 0, rootMargin: "-50% 0% -50% 0%" })
    observer.observe(section)
    return observer
  }

  setCurrent(current) {
    for (const link of this.linkTargets) {
      const active = link === current
      link.classList.toggle("AnchorNav-link--is-active", active)
      link.firstElementChild.classList.toggle("AnchorNav-link-label--is-active", active)
      link.dataset.active = String(active)
      if (active) link.setAttribute("aria-current", "true")
      else link.removeAttribute("aria-current")
    }
    this.currentLabelTarget.textContent = current.textContent
  }

  updateStuck() {
    const stuck = window.scrollY > this.initialTop
    this.navTarget.classList.toggle("AnchorNav--stuck", stuck)
    this.navTarget.dataset.sticky = String(stuck)
    // A fixed bar leaves the document; the spacer keeps the content from jumping.
    this.spacerTarget.style.height = `${this.navTarget.offsetHeight}px`
    this.spacerTarget.hidden = !stuck
  }

  toggle(event) {
    event.preventDefault()
    if (this.menuButtonTarget.getAttribute("aria-expanded") === "true") this.close()
    else this.open()
  }

  open() {
    this.setOpen(true)
    this.linkTargets[0]?.focus()
  }

  close() {
    this.setOpen(false)
  }

  // On narrow screens a link tap should also fold the menu away.
  linkClicked() {
    if (window.matchMedia("(max-width: 63.24rem)").matches) this.close()
  }

  closeOnFocusOut(event) {
    if (!this.innerTarget.contains(event.relatedTarget)) this.close()
  }

  setOpen(open) {
    this.menuButtonTarget.setAttribute("aria-expanded", String(open))
    this.closedIconTarget.hidden = open
    this.openIconTarget.hidden = !open
    this.navTarget.classList.toggle("AnchorNav--expanded", open)
    this.innerTarget.classList.toggle("AnchorNav-inner-container--expanded", open)
    this.overlayTarget.classList.toggle("AnchorNav-overlay--expanded", open)
    document.body.classList.toggle("AnchorNav--no-bg-scroll", open)
  }
}
